/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { SystemOptions } from '../models/system-options';
import { SystemCategories } from '../models/system-categories';
import {
  SYSTEM_CATEGORY_OPTIONS_FACADE_TOKEN,
  ISystemCategoryOptionsFacade,
} from '../../../../data-access/src/lib/domain/facades-models/sysque-system-category-options-facade-interface';
import { GlobalConstants } from '../constants/global-constants';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { SysqueElectricalState } from '../models/sysque-electrical-state';
import { CategoryOptionsReturnCodes } from '../models/category-options-return-codes';
import { CableTrayConduitBase } from '../../../../../shared/ui/src/lib/enums/cable-tray-conduit-base';
import { ActivatedRoute } from '@angular/router';
import { SysQueCategoryOperationsResult, SysQueSystemsOperationsResult } from '../models/sysque-systems-operations';
import { AcrylicSurfaceService } from '../acrylic-surface/acrylic-surface.service';
import { AlertStatus, IAlert } from "../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-system-category-options',
  templateUrl: './sysque-system-category-options.component.html',
  styleUrls: ['./sysque-system-category-options.component.scss'],
  standalone: false
})
export class SysqueSystemCategoryOptionsComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter();
  public data: SystemOptions = {
    buttonLabel: '',
  };
  public componentTitle = '';
  public systemName = '';
  public systemOrCategoryName = '';
  public oldSystemName = '';
  public oldCategoryName = '';
  public isCreateButtonDisabled = true;
  public alertMessage: string;
  public alertStatus: AlertStatus = AlertStatus.Info;
  public alertName: string;
  public alertHeightSystem = 310;
  public alertHeightCategory = 260;
  public nonAlertHeightSystem = 240;
  public nonAlertHeightCategory = 200;
  public isAlertOn = false;
  public showClose = false;
  public systemShape = '';
  public result: Observable<SystemOptionsReturnCodes>;
  public systemState: SysqueElectricalState;
  public categoryIndex = -1;
  public shown = false;
  public displayExpr = '';
  public categories = SystemCategories.getSystemCategories();
  public windowType = '';
  public textboxPlaceHolder = '';

  public get alert(): IAlert {
    return {
      message: this.alertMessage,
      type: this.alertStatus,
      title: this.alertName,
      isDismissable: this.showClose,
    };
  }

  private cableTrayConduitBase = Object.values(CableTrayConduitBase).map((value) => value.toLocaleLowerCase());
  private readonly closed$ = new Subject<void>();

  constructor(
    @Inject(SYSTEM_CATEGORY_OPTIONS_FACADE_TOKEN) private facade: ISystemCategoryOptionsFacade,
    private route: ActivatedRoute,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  /**
   * Initialize the component
   */
  public ngOnInit(): void {
    this.route.data.pipe(takeUntil(this.closed$)).subscribe((data) => {
      this.data.buttonLabel = data.isCreateDialog ? GlobalConstants.CreateButton : GlobalConstants.ApplyButton;

      // Set the component title based on the dialog type
      if (data.isCreateDialog && data.isSystemOperation) {
        this.componentTitle = GlobalConstants.CREATE_SYSTEM_NAME_DIALOG;
      } else if (!data.isCreateDialog && data.isSystemOperation) {
        this.componentTitle = GlobalConstants.RENAME_SYSTEM_NAME_DIALOG;
      } else if (data.isCreateDialog && !data.isSystemOperation) {
        this.componentTitle = GlobalConstants.CREATE_CATEGORY_NAME_DIALOG;
      } else if (!data.isCreateDialog && !data.isSystemOperation && !data.isDuplicateOperation) {
        this.componentTitle = GlobalConstants.RENAME_CATEGORY_NAME_DIALOG;
      } else {
        this.data.buttonLabel = GlobalConstants.DuplicateButton;
        this.componentTitle = GlobalConstants.DUPLICATE_SYSTEM_NAME_DIALOG;
      }
    });

    // Set the window type based on the component title
    this.windowType =
      this.componentTitle && this.componentTitle.includes(GlobalConstants.System)
        ? GlobalConstants.System
        : GlobalConstants.Category;

    if (this.windowType === GlobalConstants.Category) {
      this.shown = true;
      this.textboxPlaceHolder = GlobalConstants.CategoryName;
    } else {
      this.textboxPlaceHolder = GlobalConstants.SystemName;
    }

    this.validateApply();
  }

  /**
   * Clean up the component
   */
  public ngOnDestroy(): void {
    this.closed$.next();
    this.closed$.complete();
  }

  /**
   * Validate the apply button
   */
  public validateApply(): void {
    this.systemState = this.facade.getSystemState();
    this.systemName = this.oldSystemName = this.systemState?.selectedSystem;

    // If the dialog is for renaming a system, disable the create button
    if (
      this.data.buttonLabel === GlobalConstants.ApplyButton ||
      this.data.buttonLabel === GlobalConstants.DuplicateButton
    ) {
      this.systemShape = this.systemState?.systemList.find(system => system.name === this.systemName).systemShape;
      this.categoryIndex = this.categories.findIndex((cat) => cat.text.trim() === this.systemShape.trim());

      this.systemOrCategoryName =
        this.windowType === GlobalConstants.Category ? this.systemState?.selectedCategory : this.systemName;
    }
  }
  
  /**
   * Check if the shape selection should be disabled
   * @returns - Whether the shape selection should be disabled
   */
  public shouldDisableShapeSelection(): boolean {
    return this.windowType === GlobalConstants.System && this.data.buttonLabel !== GlobalConstants.CreateButton;
  }

  /**
   * Handle the user input change event
   */
  public onUserInputChanged(): void {
    this.validateCreateButton();
  }

  /**
   * Handle the category selection event
   * @param event - The selected category
   */
  public getSelectedValue(event: any): void {
    if(!event.value) {
      this.systemShape = "";
      this.categoryIndex = -1;
      return;
    }
    this.systemShape = event.value.text;
    this.categoryIndex = this.categories.findIndex((cat) => cat.text.trim() === this.systemShape.trim());
  }

  /**
   * Validate the create button
   */
  public validateCreateButton(): void {
    // If the system or category name is empty, disable the create button
    if (!this.systemOrCategoryName) {
      this.hideAlert();
      this.isCreateButtonDisabled = true;
      return;
    }

    // If the system or category name does not start with an alphabetic character, disable the create button
    this.isCreateButtonDisabled = !/^[a-zA-Z]/.test(this.systemOrCategoryName);

    if (this.isCreateButtonDisabled || this.systemOrCategoryName.trim() === '') {
      this.showAlert();
    } else {
      this.hideAlert();
    }
  }

  /**
   * Handle the dismiss click event for the alert
   */
  public onDismissClick(): void {
    this.hideAlert();
  }

  /**
   * Show the alert
   */
  public showAlert(): void {
    let height;
    if (this.windowType === 'Category') {
      height = this.alertHeightCategory;
    } else {
      height = this.alertHeightSystem;
    }
    this.facade.windowResize(height);
    this.isAlertOn = true;
    this.showAlertInput();
  }

  /**
   * Show alert for input
   */
  public showAlertInput(): void {
    this.showClose = false;
    this.alertStatus = AlertStatus.Info;
    this.alertName = '';
    this.alertMessage = 'Name must start with alphabetic character';
  }

  /**
   * Show alert for selection
   */
  public showAlertSelection(): void {
    this.showAlert();
    this.showClose = true;
    this.alertStatus = AlertStatus.Warning;
    this.alertName = 'Failure';
    this.alertMessage = 'Please select ' + this.windowType + ' Type';
  }

  /**
   * Show alert message
   * @param alertMessage - The alert message to show
   */
  public showAlertMessage(alertMessage: string): void {
    this.showAlert();
    this.showClose = false;
    this.alertStatus = AlertStatus.Warning;
    this.alertName = 'Failure';
    this.alertMessage = alertMessage;
    this.isCreateButtonDisabled = true;
  }

  /**
   * Hide the alert
   */
  public hideAlert(): void {
    this.setNonAlertHeight();
    this.isAlertOn = false;
    this.showClose = false;
    this.alertMessage = '';
  }

  /**
   * Set the non-alert height for the window
   */
  public setNonAlertHeight(): void {
    let height;
    if (this.windowType === GlobalConstants.Category) {
      height = this.nonAlertHeightCategory;
    } else {
      height = this.nonAlertHeightSystem;
    }
    this.facade.windowResize(height);
  }

  /**
   * Create the new system or category
   */
  public create(): void {
    // If the system type is selected and the category is not selected, show an alert
    if (this.windowType === GlobalConstants.System && this.isCategorySelectionInvalid()) {
      this.showAlertSelection();
    } else {
      this.hideAlert();
      // Create the new system or category based on the window type
      if (this.windowType === GlobalConstants.System) {
        this.createSystem();
      } else {
        this.createCategory();
      }
    }
  }

  /**
   * Create the new system
   */
  public createSystem(): void {
    // Handle the system options request
    this.requestSystemOptions()
      .pipe(take(1))
      .subscribe((result) => {
        switch (result.returnCode) {
          case SystemOptionsReturnCodes.SystemExists:
            this.showAlertMessage(this.windowType + GlobalConstants.NameAlreadyExists);
            break;
          case SystemOptionsReturnCodes.Success:
            this.facade.updateSystemInfo(result.systems);
            this.notifyForUpdateSystemList();
            this.closeWindow();
            break;
          case SystemOptionsReturnCodes.Failed:
            this.showAlertMessage(GlobalConstants.SystemError);
            break;
        }
      });
  }

  /**
   * Create the new category
   */
  public createCategory(): void {
    // Handle the category options request
    this.requestCategoryOptions()
      .pipe(take(1))
      .subscribe((result) => {
        switch (result.returnCode) {
          case CategoryOptionsReturnCodes.Success: {
            this.facade.updateStateService(result.categories);
            this.notifyForUpdateCategoryList();
            this.closeWindow();
            break;
          }
          case CategoryOptionsReturnCodes.CategoryExists: {
            this.showAlertMessage(this.windowType + GlobalConstants.NameAlreadyExists);
            break;
          }
          case CategoryOptionsReturnCodes.GenericTab: {
            this.showAlertMessage(GlobalConstants.GenericTab);
            break;
          }
          case CategoryOptionsReturnCodes.Error: {
            this.showAlertMessage(GlobalConstants.ErrorCategoryOptions);
            break;
          }
        }
      });
  }

  /**
   * Notify for update system list
   */
  public notifyForUpdateSystemList(): void {
    if (this.data.buttonLabel == GlobalConstants.CreateButton) {
      localStorage.removeItem(GlobalConstants.SYSTEM_CREATION_SUCCESS);
      localStorage.setItem(GlobalConstants.SYSTEM_CREATION_SUCCESS, 'true');
    } else if (this.data.buttonLabel == GlobalConstants.ApplyButton) {
      localStorage.removeItem(GlobalConstants.SYSTEM_RENAMED_SUCCESS);
      localStorage.setItem(GlobalConstants.SYSTEM_RENAMED_SUCCESS, 'true');
    } else {
      localStorage.removeItem(GlobalConstants.SYSTEM_DUPLICATED_SUCCESS);
      localStorage.setItem(GlobalConstants.SYSTEM_DUPLICATED_SUCCESS, 'true');
    }
  }

  /**
   * Notify for update category list
   */
  public notifyForUpdateCategoryList(): void {
    if (this.data.buttonLabel == GlobalConstants.CreateButton) {
      localStorage.removeItem(GlobalConstants.CATEGORY_CREATION_SUCCESS);
      localStorage.setItem(GlobalConstants.CATEGORY_CREATION_SUCCESS, 'true');
    } else {
      localStorage.removeItem(GlobalConstants.CATEGORY_RENAMED_SUCCESS);
      localStorage.setItem(GlobalConstants.CATEGORY_RENAMED_SUCCESS, 'true');
    }
  }

  /**
   * Request the system options needed for the operation
   * @returns - The system options return codes
   */
  public requestSystemOptions(): Observable<SysQueSystemsOperationsResult> {
    // Get and call the appropriate request based on the dialog type
    switch (this.data.buttonLabel) {
      case GlobalConstants.CreateButton:
        return this.facade.requestSystemOptions(
          GlobalConstants.CreateSystemRequestName,
          this.systemOrCategoryName,
          this.systemShape
        );
      case GlobalConstants.ApplyButton:
        return this.facade.requestSystemOptions(
          GlobalConstants.RenameSystemRequestName,
          this.systemOrCategoryName,
          this.systemShape,
          this.oldSystemName
        );
      case GlobalConstants.DuplicateButton:
        return this.facade.requestSystemOptions(
          GlobalConstants.DuplicateSystemRequestName,
          this.systemOrCategoryName,
          this.oldSystemName
        );
    }
  }

  /**
   * Request the category options needed for the operation
   * @returns - The category options return codes
   */
  public requestCategoryOptions(): Observable<SysQueCategoryOperationsResult> {
    // If the category name is a cable tray or conduit base, show an alert
    this.oldCategoryName = this.systemState?.selectedCategory;
    if (this.cableTrayConduitBase.includes(this.systemOrCategoryName.toLowerCase())) {
      const result = new SysQueCategoryOperationsResult();
      result.returnCode = CategoryOptionsReturnCodes.GenericTab;
      result.categories = null;
      return of(result);
    }

    // Get the appropriate request name based on the dialog type
    const requestName =
      this.data.buttonLabel == GlobalConstants.CreateButton
        ? GlobalConstants.CreateCategoryRequestName
        : GlobalConstants.RenameCategoryRequestName;

    // Call the appropriate request based on the dialog type
    return this.facade.requestCategoryOptions(
      requestName,
      this.systemOrCategoryName,
      this.systemName,
      this.oldCategoryName
    );
  }

  /**
   * Close the window
   */
  public closeWindow(): void {
    this.facade.closeWindow();
    this.close.emit();
    if (localStorage.getItem(GlobalConstants.ACRYLIC_SURFACE)) {
      this.acrylicSurfaceService.hide();
      localStorage.removeItem(GlobalConstants.ACRYLIC_SURFACE);
    }
  }

  /**
   * Check if the category selection is invalid
   * @returns - Whether the category selection is invalid
   */
  private isCategorySelectionInvalid(): boolean {
    // If the category is not selected, return true
    if (!this.systemShape) {
      return true;
    }

    // If the category is not in the list of categories, return true
    // This is to handle the case where the category is not in the list of categories
    return this.categories.findIndex((cat) => cat.text.trim() === this.systemShape.trim()) === -1;
  }
}

export enum SystemOptionsReturnCodes {
  SystemExists = 0,
  Success = 1,
  Failed = 2,
}
